import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, AlertTriangle, CheckCircle, Loader2, Edit3, Save } from 'lucide-react';

const TRActionModal = ({ 
  isOpen, 
  onClose, 
  selectedData = [], 
  action,
  onConfirm,
  apiBaseUrl = '' 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedCupons, setEditedCupons] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Inicializa os cupons editáveis quando o modal abre
  useEffect(() => {
    if (isOpen) {
      const initialData = {};
      relevantItems.forEach(item => {
        initialData[item.id] = {
          title: item.title || '',
          description: item.description || ''
        };
      });
      setEditedCupons(initialData);
      setIsEditMode(false);
      setHasChanges(false);
    }
  }, [isOpen, selectedData]);

  // Fecha modal ao pressionar ESC
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && !isLoading) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, isLoading]);

  if (!isOpen) return null;

  const isAddAction = action === 'add';
  const actionText = isAddAction ? 'adicionar' : 'remover';
  const actionTextCaps = isAddAction ? 'Adicionar' : 'Remover';
  const preposition = isAddAction ? 'ao' : 'do';

  // Filtra itens baseado na ação
  const relevantItems = selectedData.filter(item => {
    if (isAddAction) {
      return !item.is_on_tr;
    } else {
      return item.is_on_tr;
    }
  });

  const irrelevantCount = selectedData.length - relevantItems.length;

  // Handler para edição de campos
  const handleFieldChange = (cupomId, field, value) => {
    setEditedCupons(prev => ({
      ...prev,
      [cupomId]: {
        ...prev[cupomId],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  // Salvar apenas no banco (sem enviar ao TR)
  const handleSaveOnly = async () => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem("access_token");
      const cuponsToUpdate = relevantItems.map(item => ({
        id: item.id,
        title: editedCupons[item.id]?.title || item.title,
        description: editedCupons[item.id]?.description || item.description
      }));

      const response = await fetch(`${apiBaseUrl}/api/bulk-update/`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ cupons: cuponsToUpdate })
      });

      if (!response.ok) throw new Error('Erro ao salvar alterações');

      const result = await response.json();
      
      // Callback de sucesso sem fechar o modal
      if (onConfirm.onSaveSuccess) {
        onConfirm.onSaveSuccess(result);
      }
      
      setHasChanges(false);
      setIsEditMode(false);
      
    } catch (error) {
      console.error('Erro ao salvar:', error);
      if (onConfirm.onError) {
        onConfirm.onError('Erro ao salvar alterações. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Salvar e enviar ao TR
  const handleConfirmTR = async () => {
    setIsLoading(true);
    try {
      // Primeiro salva as alterações se houver
      if (hasChanges) {
        const token = localStorage.getItem("access_token");
        const cuponsToUpdate = relevantItems.map(item => ({
          id: item.id,
          title: editedCupons[item.id]?.title || item.title,
          description: editedCupons[item.id]?.description || item.description
        }));

        await fetch(`${apiBaseUrl}/api/bulk-update/`, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ cupons: cuponsToUpdate })
        });
      }

      // Depois envia ao TR
      await onConfirm(relevantItems, action);
      onClose();
      
    } catch (error) {
      console.error('Erro na ação TR:', error);
      if (onConfirm.onError) {
        onConfirm.onError('Erro ao processar ação. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className={`text-xl font-semibold flex items-center gap-2 ${
            isAddAction ? 'text-green-700' : 'text-red-700'
          }`}>
            {isAddAction ? (
              <Plus className="h-5 w-5 text-green-600" />
            ) : (
              <Minus className="h-5 w-5 text-red-600" />
            )}
            {actionTextCaps} cupons {preposition} TR
          </h2>
          <div className="flex items-center gap-2">
            {!isEditMode && relevantItems.length > 0 && (
              <button
                onClick={() => setIsEditMode(true)}
                disabled={isLoading}
                className="flex items-center gap-2 px-3 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                <Edit3 className="h-4 w-4" />
                Editar Cupons
              </button>
            )}
            <button
              onClick={onClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 p-6 overflow-auto">
          {relevantItems.length === 0 ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum cupom válido selecionado
              </h3>
              <p className="text-gray-600">
                {isAddAction 
                  ? 'Todos os cupons selecionados já estão no TR.'
                  : 'Nenhum dos cupons selecionados está no TR.'}
              </p>
              {irrelevantCount > 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  {irrelevantCount} cupom(s) {isAddAction ? 'já está(ão)' : 'não está(ão)'} no TR.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info banner */}
              <div className={`p-4 rounded-lg border-l-4 ${
                isAddAction 
                  ? 'bg-green-50 border-green-400' 
                  : 'bg-red-50 border-red-400'
              }`}>
                <div className="flex items-center">
                  {isAddAction ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                  )}
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {isEditMode ? 'Modo de edição ativo' : 'Confirmar ação'}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {isEditMode 
                        ? 'Edite os títulos e descrições dos cupons antes de enviar.'
                        : `Você está prestes a ${actionText} ${relevantItems.length} cupom(s) ${preposition} dashboard do Thiago Rodrigo.`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {irrelevantCount > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Nota:</strong> {irrelevantCount} cupom(s) selecionado(s) {isAddAction ? 'já está(ão)' : 'não está(ão)'} no TR e será(ão) ignorado(s).
                  </p>
                </div>
              )}

              {hasChanges && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <p className="text-sm text-blue-800">
                    <strong>Alterações não salvas</strong> - Salve antes de enviar ao TR
                  </p>
                  <button
                    onClick={handleSaveOnly}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Salvar Alterações
                  </button>
                </div>
              )}

              {/* Lista de cupons */}
              <div className="space-y-3">
                <div className="bg-gray-50 px-4 py-2 rounded-t-lg border-b">
                  <h5 className="font-medium text-gray-900">
                    Cupons que serão {isAddAction ? 'adicionados' : 'removidos'} ({relevantItems.length}):
                  </h5>
                </div>
                
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {relevantItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isEditMode 
                          ? 'border-blue-300 bg-blue-50' 
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="space-y-3">
                        {/* Título */}
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Título
                          </label>
                          {isEditMode ? (
                            <input
                              type="text"
                              value={editedCupons[item.id]?.title || ''}
                              onChange={(e) => handleFieldChange(item.id, 'title', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              placeholder="Digite o título do cupom"
                            />
                          ) : (
                            <p className="text-sm font-medium text-gray-900">
                              {editedCupons[item.id]?.title || item.title || 'Sem título'}
                            </p>
                          )}
                        </div>

                        {/* Descrição */}
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Descrição
                          </label>
                          {isEditMode ? (
                            <textarea
                              value={editedCupons[item.id]?.description || ''}
                              onChange={(e) => handleFieldChange(item.id, 'description', e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                              placeholder="Digite a descrição do cupom"
                            />
                          ) : (
                            <p className="text-sm text-gray-600">
                              {editedCupons[item.id]?.description || item.description || 'Sem descrição'}
                            </p>
                          )}
                        </div>

                        {/* Info adicional */}
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div className="text-xs text-gray-500">
                            <span className="font-medium">{item.store_name}</span> • {item.spider}
                            {item.code && (
                              <span className="ml-2 font-mono text-blue-600">{item.code}</span>
                            )}
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.is_on_tr 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.is_on_tr ? 'No TR' : 'Fora do TR'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t bg-gray-50">
          <div className="flex items-center gap-2">
            {isEditMode && (
              <button
                onClick={() => {
                  setIsEditMode(false);
                  setHasChanges(false);
                }}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancelar Edição
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Fechar
            </button>
            
            {relevantItems.length > 0 && (
              <>
                {isEditMode && hasChanges && (
                  <button
                    onClick={handleSaveOnly}
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Salvar no Banco
                  </button>
                )}
                
                <button
                  onClick={handleConfirmTR}
                  disabled={isLoading}
                  className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 ${
                    isAddAction
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      {isAddAction ? (
                        <Plus className="h-4 w-4" />
                      ) : (
                        <Minus className="h-4 w-4" />
                      )}
                      {hasChanges ? 'Salvar e ' : ''}{actionTextCaps} no TR
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TRActionModal;